import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { getPlanFeatures } from "../utils/planFeatures";
import PlanLockedBanner from "./PlanLockedBanner";
import { EmojiReactionField } from "./EmojiPicker";
import "./ClientFeatures.css";

const emptyForm = {
  id: null,
  comment: "",
  reply_type: "exact-words",
  keyword: "",
  reply_text: "",
  reaction: "",
  enable_typing: true,
  select_all: true,
  client_id: "",
};

const parseContent = (raw) => {
  try {
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
    return obj?.text || "";
  } catch {
    return typeof raw === "string" ? raw : "";
  }
};

const instanceLabel = (inst) => {
  if (!inst) return "Session";
  const name = inst.name || inst.title || "Untitled";
  const preview = inst.client_id_preview || "";
  const state = inst.status_label || "";
  return [name, preview, state].filter(Boolean).join(" · ");
};

const AutomationLayer = () => {
  const [bots, setBots] = useState([]);
  const [instances, setInstances] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [planBlocked, setPlanBlocked] = useState(false);

  const showAlert = (message, cls = "success") => {
    setAlert({ message, class: cls });
    setTimeout(() => setAlert(null), 2500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [botRes, listRes, legacyRes] = await Promise.all([
        apiCall({ url: "bot/get", method: "GET" }),
        apiCall({ url: "sessions/instance-list?page=1&limit=100", method: "GET" }).catch(() => null),
        apiCall({ url: "sessions/get-users-instances?page=1&limit=100", method: "GET" }).catch(() => null),
      ]);
      if (botRes?.success) setBots(botRes.data || []);
      const fromList = Array.isArray(listRes?.data) ? listRes.data : [];
      const fromLegacy = Array.isArray(legacyRes?.data) ? legacyRes.data : [];
      const merged = fromList.length ? fromList : fromLegacy;
      setInstances(merged);
    } catch (err) {
      console.error(err);
      showAlert("Failed to load chatbot rules", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Chatbot";
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      setPlanBlocked(!getPlanFeatures(user).chatbot);
    } catch {
      setPlanBlocked(false);
    }
    load();
    (async () => {
      try {
        const res = await apiCall({ url: "user/get-user-by-token", method: "GET" });
        if (res?.success && res.data) {
          localStorage.setItem("user", JSON.stringify(res.data));
          setPlanBlocked(!getPlanFeatures(res.data).chatbot);
        }
      } catch {
        /* keep cached */
      }
    })();
  }, []);

  const resetForm = () => setForm(emptyForm);

  const startEdit = (bot) => {
    setForm({
      id: bot.id,
      comment: bot.comment || "",
      reply_type: bot.reply_type || "exact-words",
      keyword: bot.keyword || "",
      reply_text: parseContent(bot.content),
      reaction: bot.reaction || "",
      enable_typing: !!Number(bot.enable_typing),
      select_all: bot.select_all == null ? true : !!Number(bot.select_all),
      client_id: bot.client_id || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.reply_text.trim()) {
      showAlert("Reply message is required", "danger");
      return;
    }
    if (form.reply_type !== "any" && !form.keyword.trim()) {
      showAlert("Keyword is required for this trigger type", "danger");
      return;
    }
    if (!form.select_all && !form.client_id) {
      showAlert("Select a session, or enable Apply to all sessions", "danger");
      return;
    }
    setSaving(true);
    try {
      const body = {
        id: form.id,
        comment: form.comment.trim() || "Auto reply",
        reply_type: form.reply_type,
        reply_choice: "write-text",
        keyword: form.keyword.trim(),
        content: { text: form.reply_text.trim() },
        reaction: form.reaction.trim(),
        enable_typing: form.enable_typing,
        select_all: form.select_all ? 1 : 0,
        client_id: form.select_all ? "" : form.client_id,
        phonebook: null,
      };
      const response = await apiCall({
        url: form.id ? "bot/update" : "bot/add",
        method: "POST",
        body,
      });
      if (response?.success) {
        showAlert(response.msg || "Saved");
        setPlanBlocked(false);
        resetForm();
        load();
      } else {
        if (/plan|chatbot|allow/i.test(response?.msg || "")) setPlanBlocked(true);
        showAlert(response?.msg || "Could not save", "danger");
      }
    } catch (err) {
      showAlert(err.message || "Server error", "danger");
    } finally {
      setSaving(false);
    }
  };

  const toggleBot = async (bot) => {
    try {
      const response = await apiCall({
        url: "bot/off-on",
        method: "POST",
        body: { id: bot.id, status: !Number(bot.active) },
      });
      if (response?.success) {
        showAlert(response.msg);
        load();
      } else showAlert(response?.msg || "Failed", "danger");
    } catch (err) {
      showAlert(err.message, "danger");
    }
  };

  const deleteBot = async (bot) => {
    if (!window.confirm("Delete this chatbot rule?")) return;
    try {
      const response = await apiCall({
        url: "bot/del-bot",
        method: "POST",
        body: { id: bot.id },
      });
      if (response?.success) {
        showAlert(response.msg);
        if (form.id === bot.id) resetForm();
        load();
      } else showAlert(response?.msg || "Failed", "danger");
    } catch (err) {
      showAlert(err.message, "danger");
    }
  };

  const needsKeyword = form.reply_type !== "any";

  const sessionNameFor = (clientId) => {
    const inst = instances.find((i) => i.client_id === clientId);
    return inst ? instanceLabel(inst) : "One session";
  };

  const setSelectAll = (checked) => {
    if (checked) {
      setForm((f) => ({ ...f, select_all: true, client_id: "" }));
      return;
    }
    const first = instances[0]?.client_id || "";
    setForm((f) => ({
      ...f,
      select_all: false,
      client_id: f.client_id || first,
    }));
  };

  return (
    <div className="cf-wrap">
      {alert && (
        <div className={`alert alert-${alert.class} cf-alert`}>{alert.message}</div>
      )}

      {planBlocked ? (
        <PlanLockedBanner
          title="Chatbot not in your plan"
          message="Keyword auto-replies require a plan that includes Chatbot."
        />
      ) : null}

      <div className="row g-3">
        <div className="col-lg-5">
          <div className="cf-card">
            <div className="cf-card-head">
              <h5>{form.id ? "Edit rule" : "New chatbot rule"}</h5>
              <p>Reply automatically when customers message matching keywords.</p>
            </div>
            <form onSubmit={handleSubmit} className="cf-form">
              <label>
                Name
                <input
                  className="form-control"
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  placeholder="e.g. Business hours greeting"
                />
              </label>

              <label>
                Trigger
                <select
                  className="form-select"
                  value={form.reply_type}
                  onChange={(e) => setForm({ ...form, reply_type: e.target.value })}
                >
                  <option value="exact-words">Exact keyword match</option>
                  <option value="matching-words">Contains matching words</option>
                  <option value="any">Any incoming message</option>
                </select>
              </label>

              {needsKeyword && (
                <label>
                  Keyword
                  <input
                    className="form-control"
                    value={form.keyword}
                    onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                    placeholder="hello, price, support…"
                  />
                </label>
              )}

              <label>
                Reply message
                <textarea
                  className="form-control"
                  rows={4}
                  value={form.reply_text}
                  onChange={(e) => setForm({ ...form, reply_text: e.target.value })}
                  placeholder="Thanks for messaging us — how can we help?"
                  required
                />
              </label>

              <EmojiReactionField
                value={form.reaction}
                onChange={(reaction) => setForm((f) => ({ ...f, reaction }))}
              />

              <div className="cf-toggle-stack">
                <button
                  type="button"
                  className={`cf-toggle-btn ${form.enable_typing ? "is-on" : ""}`}
                  onClick={() => setForm((f) => ({ ...f, enable_typing: !f.enable_typing }))}
                  aria-pressed={form.enable_typing}
                >
                  <span className="cf-toggle-switch" aria-hidden="true">
                    <span className="cf-toggle-knob" />
                  </span>
                  <span className="cf-toggle-copy">
                    <strong>Typing indicator</strong>
                    <small>Show typing before the auto-reply is sent</small>
                  </span>
                </button>

                <button
                  type="button"
                  className={`cf-toggle-btn ${form.select_all ? "is-on" : ""}`}
                  onClick={() => setSelectAll(!form.select_all)}
                  aria-pressed={form.select_all}
                >
                  <span className="cf-toggle-switch" aria-hidden="true">
                    <span className="cf-toggle-knob" />
                  </span>
                  <span className="cf-toggle-copy">
                    <strong>Apply to all sessions</strong>
                    <small>
                      {form.select_all
                        ? "Rule runs on every WhatsApp session"
                        : "Rule runs on one selected session only"}
                    </small>
                  </span>
                </button>
              </div>

              {!form.select_all && (
                <label>
                  Session
                  <select
                    className="form-select"
                    value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    required
                  >
                    <option value="">Select session</option>
                    {instances.map((inst) => (
                      <option key={inst.client_id || inst.id} value={inst.client_id}>
                        {instanceLabel(inst)}
                      </option>
                    ))}
                  </select>
                  {!instances.length && (
                    <small className="text-secondary d-block mt-1">
                      No sessions found.{" "}
                      <Link to="/scan-instance">Connect a WhatsApp session</Link> first.
                    </small>
                  )}
                </label>
              )}

              <div className="cf-actions">
                <button type="submit" className="btn btn-success" disabled={saving || planBlocked}>
                  {saving ? "Saving…" : form.id ? "Update rule" : "Add rule"}
                </button>
                {form.id && (
                  <button type="button" className="btn btn-light" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="cf-card cf-rules-panel">
            <div className="cf-card-head cf-rules-head">
              <div>
                <h5>Your rules</h5>
                <p>{loading ? "Loading…" : `${bots.length} chatbot rule${bots.length === 1 ? "" : "s"}`}</p>
              </div>
              {!loading && bots.length > 0 && (
                <span className="cf-rules-count">{bots.filter((b) => Number(b.active)).length} active</span>
              )}
            </div>
            {!loading && bots.length === 0 && (
              <div className="cf-empty">
                <Icon icon="mdi:robot-outline" width={36} />
                <p>No chatbot rules yet. Create a keyword reply to get started.</p>
              </div>
            )}
            <div className="cf-list">
              {bots.map((bot) => {
                const active = !!Number(bot.active);
                const triggerIcon =
                  bot.reply_type === "any"
                    ? "mdi:message-flash-outline"
                    : bot.reply_type === "matching-words"
                      ? "mdi:text-search"
                      : "mdi:key-variant";
                const triggerLabel =
                  bot.reply_type === "any"
                    ? "Any message"
                    : bot.reply_type === "matching-words"
                      ? `Contains “${bot.keyword}”`
                      : `Exact “${bot.keyword}”`;
                const scopeLabel = Number(bot.select_all)
                  ? "All sessions"
                  : sessionNameFor(bot.client_id);
                const reply = parseContent(bot.content);

                return (
                  <article key={bot.id} className={`cf-rule ${active ? "is-on" : "is-off"}`}>
                    <div className="cf-rule-top">
                      <div className="cf-rule-icon" aria-hidden="true">
                        <Icon icon={triggerIcon} width={20} />
                      </div>
                      <div className="cf-rule-main">
                        <div className="cf-rule-title-row">
                          <h6>{bot.comment || "Untitled rule"}</h6>
                          <span className={`cf-pill ${active ? "on" : "off"}`}>
                            {active ? "On" : "Off"}
                          </span>
                        </div>
                        <div className="cf-rule-chips">
                          <span className="cf-chip">
                            <Icon icon={triggerIcon} width={14} />
                            {triggerLabel}
                          </span>
                          <span className="cf-chip">
                            <Icon icon="mdi:whatsapp" width={14} />
                            {scopeLabel}
                          </span>
                          {bot.reaction ? (
                            <span className="cf-chip cf-chip-emoji">{bot.reaction}</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    {reply ? (
                      <blockquote className="cf-rule-reply">
                        <Icon icon="mdi:format-quote-open" width={16} />
                        <p>{reply}</p>
                      </blockquote>
                    ) : null}
                    <div className="cf-rule-actions">
                      <button
                        type="button"
                        className={`cf-act ${active ? "cf-act-mute" : "cf-act-go"}`}
                        onClick={() => toggleBot(bot)}
                        title={active ? "Turn off" : "Turn on"}
                      >
                        <Icon icon={active ? "mdi:pause" : "mdi:play"} width={16} />
                        {active ? "Turn off" : "Turn on"}
                      </button>
                      <button
                        type="button"
                        className="cf-act cf-act-edit"
                        onClick={() => startEdit(bot)}
                        title="Edit"
                      >
                        <Icon icon="mdi:pencil-outline" width={16} />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="cf-act cf-act-danger"
                        onClick={() => deleteBot(bot)}
                        title="Delete"
                      >
                        <Icon icon="mdi:trash-can-outline" width={16} />
                        Delete
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationLayer;
